import { Card, Divider, Grid, Typography } from "@material-ui/core";
import React, { useEffect, useState } from "react";

/**
 * Generates a random color in the format #XXXXXX
 * The color generated is a muted color, with a bias towards green
 * @returns {string} - A string in the format #XXXXXX
 */
function getRandomColor() {
  const r = Math.floor(Math.random() * 156)
    .toString(16)
    .padStart(2, "0");
  const g = Math.floor(Math.random() * 156 + 30)
    .toString(16)
    .padStart(2, "0");
  const b = Math.floor(Math.random() * 156 + 30)
    .toString(16)
    .padStart(2, "0");
  return `#${r}${g}${b}`;
}
/**
 * A recursive component that displays a hierarchical structure of nodes.
 * Each node can be clicked to reveal its children in a nested manner.
 * The component uses a tree-like data structure to render the nodes.
 *
 * @param {object[]} nodeData - Array of nodes to be displayed. Each node includes:
 *   @param {string} nodeData[].name - The name of the node.
 *   @param {number} nodeData[].sales - The sales value associated with the node.
 *   @param {object[]} [nodeData[].children] - Optional array of child nodes.
 */
export function RecursiveStructureReport({ nodeData }) {
  return (
    <>
      {nodeData.map(node => (
        <NodeComponent
          key={node.name}
          node={node}
          level={nodeData[0].level}
          color={getRandomColor()}
        />
      ))}
    </>
  );
}

/**
 * A recursive component that displays a hierarchical structure of nodes.
 * Each node can be clicked to reveal its children in a nested manner.
 * The component uses a tree-like data structure to render the nodes.
 *   @param {object} props - The component's props.
 *   @param {object} props.node - The node to be displayed. Includes:
 *   @param {string} props.color - A randomly generated color to be used for the node's border.
 *   @param {string} props.level - The level of the node in the tree structure.
 */
function NodeComponent({ node, color, level }) {
  const [open, setOpen] = useState(false); // Local state for this node
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const isParent = node?.children?.length > 0;
  const isOpenParent = isParent && open;
  const isClosedParent = isParent && !open;
  const isFirstChild = node.level === level;
  const hasSales =
    node.total.created +
      node.total.paid +
      node.total.installed +
      node.total.activated >
      0 || isFirstChild;
  const colors = [
    "#AAFF56", // Peach
    "#A7F3D0", // Light Mint Green
    "#BFDBFE", // Light Blue
    "#FEF9C3", // Pale Cream
    "#E9D5FF", // Lavender
    "#CFFAFE", // Pale Aqua Blue
    "#D9F99D", // Soft Lime
    "#FDE68A", // Warm Butter Yellow
    "#D4E6F1", // Soft Periwinkle
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div>
      {/* {isOpenParent && !isFirstChild && (
        <Divider
          style={{
            marginBottom: ".5rem",
            height: "2px",
            marginLeft: isMobile ? "" : ".75rem",
            marginRight: isMobile ? "" : ".75rem",
            width: isMobile ? "100%" : "",
            backgroundColor: colors[node.level - 1],
          }}
        />
      )} */}
      <Card
        style={{
          width: "100%",
          paddingLeft: isOpenParent ? ".3rem" : "",
          padding: isOpenParent && isFirstChild ? ".3rem" : "0",
          paddingBottom: "0",
          border: `${isOpenParent && isFirstChild ? "3px solid" : "none"}`,
          borderColor: `${isOpenParent ? "#38b5b5" : "#e68625"}`,
          marginBottom: `${
            (isClosedParent && hasSales) || (isFirstChild && hasSales)
              ? ".5rem"
              : "0rem"
          }`,

          borderRadius: `${isParent || isFirstChild ? "16px" : "0"}`,
          boxShadow: `${
            isClosedParent || isFirstChild
              ? "0px 4px 12px rgba(0, 0, 0, 0.1)"
              : "none"
          }`,
        }}
      >
        <div>
          <span
            role="button"
            tabIndex={0}
            style={{
              display: "flex",
              justifyContent: "center",
              paddingBottom: `${isOpenParent ? ".5rem" : "0"}`,
              cursor: "pointer",
            }}
            onClick={() => {
              // Trigger haptic feedback
              if (navigator.vibrate && isParent) {
                // navigator.vibrate(17); // vibrate for 200ms
                // navigator.vibrate([7, 3, 7]); // Vibrate for 10ms
                // navigator.vibrate(19); // vibrate for 200ms
                // navigator.vibrate([7, 4, 3.5, 1, 3.5]); // Vibrate for 10ms
                navigator.vibrate(9); // vibrate for 200ms
                navigator.vibrate([0.5, 2.5, 1, 2.5]); // Vibrate for 10ms
                // navigator.vibrate(10); // vibrate for 200ms
                //  navigator.vibrate([4, 1.5, 1, 1.5, 1, 1]); // Vibrate for 10ms
              }
              setOpen(!open);
            }}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                // Trigger haptic feedback
                if (navigator.vibrate) {
                  navigator.vibrate(17); // vibrate for 200ms
                  navigator.vibrate([7, 3, 7]); // Vibrate for 10ms
                }
                setOpen(!open);
              }
            }}
          >
            {hasSales && (
              <Card
                style={{
                  width: "100%",
                  paddingTop: ".5rem",
                  border: `${isParent ? "2px solid" : "none"}`,
                  borderColor: `${isOpenParent ? "#38b5b5" : "#e68625"}`,
                  backgroundColor: `${
                    isOpenParent && isFirstChild ? "#e7e7e7" : "white"
                  }`,
                  borderRadius: `${isParent || isFirstChild ? "16px" : "0"}`,
                  boxShadow: `${
                    isClosedParent && isFirstChild
                      ? "0px 4px 12px rgba(0, 0, 0, 0.1)"
                      : "none"
                  }`,
                }}
              >
                <Grid
                  container
                  spacing={1}
                  style={{
                    padding: "0 1rem 0.5rem",
                    justifyContent: "space-between",
                    alignContent: "space-between",
                  }}
                >
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    style={{
                      display: "flex",
                      justifyContent: "flex-start",
                    }}
                  >
                    {" "}
                    <Card
                      style={{
                        padding: ".2rem",
                        marginRight: ".5rem",
                        fontSize: ".9rem",
                        borderRadius: "8px",
                        fontWeight: "bold",
                        backgroundColor: `${
                          isOpenParent && isFirstChild
                            ? "white"
                            : colors[node.level - 1]
                        }`,
                        boxShadow: "none",
                      }}
                    >
                      Level {node.level}
                    </Card>
                    <Typography
                      variant="body2"
                      color="primary"
                      style={{
                        fontWeight: "bold",
                        paddingTop: ".2rem",
                      }}
                    >
                      {node.name}:{" "}
                    </Typography>
                    {isClosedParent && isMobile && (
                      <Grid item style={{ flexGrow: 1, textAlign: "right" }}>
                        <Typography
                          variant="subtitle2"
                          style={{
                            fontWeight: "bold",
                            paddingRight: ".5rem",
                            paddingTop: ".2rem",
                          }}
                        >
                          Total
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  {isMobile && (
                    <Grid xs={12}>
                      <Divider />
                    </Grid>
                  )}

                  <Grid
                    item
                    xs={12}
                    sm={5}
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      flexWrap: "wrap", // Wrap content on smaller screens
                      ...(isMobile && {
                        justifyContent: "flex-end",
                      }),
                    }}
                  >
                    {isClosedParent && !isMobile && (
                      <Typography
                        variant="subtitle2"
                        style={{
                          fontWeight: "bold",
                          paddingRight: ".5rem",
                          paddingTop: ".15rem",
                        }}
                      >
                        Total:
                      </Typography>
                    )}
                    {[
                      {
                        label: "CRT",
                        value: isClosedParent
                          ? node.total.created
                          : node.created,
                      },
                      {
                        label: "PD",
                        value: isClosedParent ? node.total.paid : node.paid,
                      },
                      {
                        label: "INST",
                        value: isClosedParent
                          ? node.total.installed
                          : node.installed,
                      },
                      {
                        label: "ACT",
                        value: isClosedParent
                          ? node.total.activated
                          : node.activated,
                      },
                    ].map((item, index) => (
                      <React.Fragment key={item.label}>
                        {index > 0 && (
                          <Divider
                            orientation="vertical"
                            flexItem
                            style={{
                              margin: "0 .5rem",
                              backgroundColor: "slategray", // Match your color theme
                            }}
                          />
                        )}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            minWidth: "40px",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            style={{
                              color: "#5e6472",
                              paddingRight: "0.5rem",
                              width: "35px",
                              textAlign: "right",
                            }}
                          >
                            {item.label}:
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            style={{
                              fontWeight: "bold",
                              color: "#5e6472",
                              textAlign: "left",
                              width: "30px",
                            }}
                          >
                            {item.value}
                          </Typography>
                        </div>
                      </React.Fragment>
                    ))}
                  </Grid>
                </Grid>
              </Card>
            )}
          </span>
          {open && node?.children && (
            <>
              {node.children.map(childNode => (
                <NodeComponent
                  key={childNode.name}
                  node={childNode}
                  color={color}
                />
              ))}
            </>
          )}
        </div>{" "}
        {isOpenParent && (
          <>
            {isFirstChild && (
              <Divider
                style={{
                  height: "2px",
                  marginTop: "0.5rem",
                  width: "100%",
                  backgroundColor: "#6f7f8f",
                }}
              />
            )}

            <Grid
              item
              xs={12}
              style={{
                display: "flex",
                justifyContent: "flex-end",
                flexWrap: "wrap", // Wrap content on smaller screens
                ...(isMobile && {
                  justifyContent: "flex-end",
                }),
                padding: "0.5rem",
                paddingRight: "1rem",
                paddingLeft: "0",
              }}
            >
              {" "}
              <Typography
                variant="body1"
                style={{ fontWeight: "bold", paddingRight: ".75rem" }}
              >
                Total:
              </Typography>
              {[
                { label: "CRT", value: node.total.created },
                { label: "PD", value: node.total.paid },
                { label: "INST", value: node.total.installed },
                { label: "ACT", value: node.total.activated },
              ].map((item, index) => (
                <React.Fragment key={item.label}>
                  {index > 0 && (
                    <Divider
                      orientation="vertical"
                      flexItem
                      style={{
                        margin: "0 .5rem",
                        fontWeight: "bold",
                        backgroundColor: "slategray", // Match your color theme
                      }}
                    />
                  )}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: "40px",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      style={{
                        color: "#1b2a4a",
                        paddingRight: "0.5rem",
                        width: "35px",
                        textAlign: "right",
                      }}
                    >
                      {item.label}:
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      style={{
                        fontWeight: "bold",
                        color: "#1b2a4a",
                        textAlign: "left",
                        width: "30px",
                      }}
                    >
                      {item.value}
                    </Typography>
                  </div>
                </React.Fragment>
              ))}
            </Grid>
          </>
        )}
      </Card>{" "}
      {isParent && open && !isFirstChild && (
        <Divider
          style={{
            marginBottom: ".5rem",
            height: "2px",
            marginLeft: isMobile ? "" : ".75rem",
            marginRight: isMobile ? "" : ".75rem",
            width: isMobile ? "100%" : "",
            backgroundColor: colors[node.level - 1],
          }}
        />
      )}
    </div>
  );
}
